import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { PO_PRODUCT_LIST_PER_PAGE } from "../../Components/NewPurchaseOrder/constants";
import {
  buildNewPurchaseOrderProductListPayload,
  fetchNewPurchaseOrderProductList,
} from "../../services/purchaseOrders/purchaseOrdersApi";
import { poQueryCache } from "./cacheTimes";
import { normalizeProductListFilters, purchaseOrderKeys } from "./queryKeys";

/**
 * Product picker infinite list — cached per merchant + vendor + search.
 * Reopening the modal or repeating a search serves from cache when fresh
 * (unless the caller passes a lower `staleTime`).
 */
export function useNewPurchaseOrderProductListInfiniteQuery({
  auth,
  merchantId,
  vendorId = "",
  filterVendorId = "",
  categoryId = "",
  search = "",
  perPage = PO_PRODUCT_LIST_PER_PAGE,
  // Optional Discount Amount filter (`disc_amt`) — item-coupon / BOGO / Mix & Match
  // amount pickers. When absent, `disc_amt` is omitted (PO / stocktake).
  discAmt,
  // Deal pickers (BOGO / Mix&Match / item coupon) only. PO never passes these.
  excludeDealProducts,
  currentDealType,
  currentDealId,
  // Deal pickers can force Node even when elastic product-list v2 is enabled.
  forceNodeApi = false,
  kitScope,
  enabled = true,
  staleTime,
  // Callers that must hit the network on every mount (the BOGO variant picker)
  // pass "always" to override the app-wide refetchOnMount:false default.
  refetchOnMount,
}) {
  const filters = useMemo(
    () =>
      normalizeProductListFilters({
        merchantId,
        vendorId,
        filterVendorId,
        categoryId,
        search,
        perPage,
        discAmt,
        excludeDealProducts,
        currentDealType,
        currentDealId,
        forceNodeApi,
        kitScope,
      }),
    [
      merchantId,
      vendorId,
      filterVendorId,
      categoryId,
      search,
      perPage,
      discAmt,
      excludeDealProducts,
      currentDealType,
      currentDealId,
      forceNodeApi,
      kitScope,
    ]
  );

  return useInfiniteQuery({
    queryKey: purchaseOrderKeys.productList(filters),
    queryFn: ({ pageParam, signal }) =>
      fetchNewPurchaseOrderProductList(
        buildNewPurchaseOrderProductListPayload(auth, {
          merchantId: filters.merchantId,
          vendorId: filters.vendorId,
          filterVendorId: filters.filterVendorId,
          categoryId: filters.categoryId,
          search: filters.search,
          page: pageParam,
          perPage: filters.perPage,
          discAmt: filters.discAmt,
          excludeDealProducts: filters.excludeDealProducts,
          currentDealType: filters.currentDealType,
          currentDealId: filters.currentDealId,
          kitScope: filters.kitScope,
        }),
        signal,
        { forceNodeApi: Boolean(filters.forceNodeApi) }
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasMore ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(enabled && merchantId && auth?.token),
    ...poQueryCache.productPicker,
    ...(staleTime !== undefined ? { staleTime } : null),
    ...(refetchOnMount !== undefined ? { refetchOnMount } : null),
  });
}
